import React, { useState } from 'react';
import { brands } from '../mock';

// Logo slider (shipping lines).
// Each entry in `brands` (mock.js) is either
//   - a name string, e.g. 'DP WORLD'  -> looks for /images/brands/dp-world.png, then .svg, .webp, .jpg
//   - an object, e.g. { name: 'DP WORLD', url: 'https://www.dpworld.com/' }
//       url  (optional) -> clicking the logo opens that website in a new tab
//       logo (optional) -> exact logo file, e.g. '/images/brands/dpworld.svg' (default: file named after `name`, see above)
// Put the logo files in frontend/public/images/brands/ (ideally ~400px wide, transparent or white background).
// If no logo file is found, the card shows the brand name as text.
const EXTENSIONS = ['png', 'svg', 'webp', 'jpg'];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const candidates = (name) => EXTENSIONS.map((ext) => `/images/brands/${slug(name)}.${ext}`);
const normalize = (b) => (
  typeof b === 'string'
    ? { name: b, url: null, logos: candidates(b) }
    : { name: b.name, url: b.url || null, logos: b.logo ? [b.logo] : candidates(b.name) }
);

function BrandCard({ brand, testId }) {
  const [idx, setIdx] = useState(0); // which candidate file we are trying
  const src = brand.logos[idx];
  // With a url the card is a link that opens the shipping line's website in a new tab; without one it is a plain card.
  const Wrapper = brand.url ? 'a' : 'div';
  const linkProps = brand.url
    ? { href: brand.url, target: '_blank', rel: 'noopener noreferrer', 'aria-label': `${brand.name} website (opens in a new tab)`, draggable: false }
    : {};
  return (
    <Wrapper className="brand-logo-card notranslate" translate="no" data-testid={testId} title={brand.name} {...linkProps}>
      {src ? (
        <img key={src} src={src} alt={brand.name} loading="lazy" draggable="false" onError={() => setIdx((i) => i + 1)} />
      ) : (
        <span className="brand-logo-fallback">{brand.name}</span>
      )}
    </Wrapper>
  );
}

export default function BrandSlider() {
  const list = brands.map(normalize);
  // Repeat the list until one "half" of the track is wider than a big screen (card = ~250px incl. gap), then
  // double it so the loop is seamless. Speed scales with the number of cards so it stays constant.
  const copies = Math.max(1, Math.ceil(2600 / (list.length * 250)));
  const base = Array.from({ length: copies }, () => list).flat();
  const doubled = [...base, ...base];
  return (
    <div className="brand-marquee" data-testid="brand-marquee">
      <div className="marquee-track" style={{ animationDuration: `${Math.max(base.length * 4, 20)}s` }}>
        {doubled.map((b, i) => (
          <BrandCard key={i} brand={b} testId={i < list.length ? `brand-badge-${i}` : undefined} />
        ))}
      </div>
    </div>
  );
}
